import { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Tag, Button, Space, Form, Input, Select, Switch, Popconfirm, message } from 'antd';
import { PlusOutlined, ImportOutlined } from '@ant-design/icons';
import { alarms } from '../../../services/api';

// 告警知识库管理（仅 admin 可进入）：CRUD + JSON 批量导入
export default function AlarmKnowledge({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=新增
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await alarms.knowledgeList();
      setList(res.data || []);
    } catch (err) {
      message.error(err.message || '加载知识库失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const openEdit = (record) => {
    setEditing(record || null);
    form.setFieldsValue(record || { match_type: 'parameter', is_active: true });
    setEditOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await alarms.updateKnowledge(editing.id, values);
        message.success('知识条目已更新');
      } else {
        await alarms.createKnowledge(values);
        message.success('知识条目已创建');
      }
      setEditOpen(false);
      load();
    } catch (err) {
      if (err?.errorFields) return; // 表单校验失败
      message.error(err.message || '保存失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await alarms.deleteKnowledge(id);
      message.success('已删除');
      load();
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  // 批量导入：粘贴 JSON 数组，逐条校验由后端完成
  const handleImport = async () => {
    let items;
    try {
      items = JSON.parse(importText);
    } catch {
      message.error('JSON 解析失败，请检查格式（应为数组）');
      return;
    }
    if (!Array.isArray(items) || items.length === 0) {
      message.error('请提供非空 JSON 数组');
      return;
    }
    setImporting(true);
    try {
      const res = await alarms.importKnowledge(items);
      const { created, skipped } = res.data || {};
      message.success(`导入完成：成功 ${created} 条，跳过 ${skipped} 条`);
      setImportOpen(false);
      setImportText('');
      load();
    } catch (err) {
      message.error(err.message || '导入失败');
    } finally {
      setImporting(false);
    }
  };

  const columns = [
    { title: '名称', dataIndex: 'name' },
    { title: '匹配方式', dataIndex: 'match_type', width: 110, render: v => (
      <Tag color={v === 'parameter' ? 'blue' : 'geekblue'}>{v === 'parameter' ? '参数名' : '标题关键字'}</Tag>
    )},
    { title: '匹配值', dataIndex: 'match_key', width: 150 },
    { title: '故障原因', dataIndex: 'cause', ellipsis: true },
    { title: '修复建议', dataIndex: 'suggestion', ellipsis: true },
    { title: '严重度建议', dataIndex: 'level_suggestion', width: 100 },
    { title: '启用', dataIndex: 'is_active', width: 70, render: v => (v ? '是' : '否') },
    { title: '操作', width: 130, render: (_, record) => (
      <Space size="small">
        <Button size="small" onClick={() => openEdit(record)}>编辑</Button>
        <Popconfirm title="确认删除该知识条目？" onConfirm={() => handleDelete(record.id)}>
          <Button size="small" danger>删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <Modal
      title="告警知识库管理"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>新增</Button>
        <Button icon={<ImportOutlined />} onClick={() => setImportOpen(true)}>批量导入(JSON)</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={list.map(r => ({ ...r, key: r.id }))}
        loading={loading}
        size="small"
        pagination={{ pageSize: 8 }}
      />

      <Modal
        title={editing ? '编辑知识条目' : '新增知识条目'}
        open={editOpen}
        onOk={handleSave}
        onCancel={() => setEditOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如：功率过高通用处置" />
          </Form.Item>
          <Form.Item name="match_type" label="匹配方式" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="parameter">按告警参数名精确匹配</Select.Option>
              <Select.Option value="title">按告警标题关键字匹配</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="match_key" label="匹配值" rules={[{ required: true, message: '请输入匹配值' }]}>
            <Input placeholder="参数名如 active_power，或标题关键字如 通信超时" />
          </Form.Item>
          <Form.Item name="cause" label="故障原因">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="suggestion" label="修复建议">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="level_suggestion" label="严重度建议">
            <Select allowClear>
              <Select.Option value="提示">提示</Select.Option>
              <Select.Option value="警告">警告</Select.Option>
              <Select.Option value="严重">严重</Select.Option>
              <Select.Option value="紧急">紧急</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量导入知识库（JSON 数组）"
        open={importOpen}
        onOk={handleImport}
        onCancel={() => setImportOpen(false)}
        confirmLoading={importing}
        destroyOnClose
      >
        <p style={{ color: '#888' }}>
          粘贴 JSON 数组，字段：name(必填)、match_type(parameter/title)、match_key(必填)、cause、suggestion、level_suggestion。
        </p>
        <Input.TextArea
          rows={8}
          value={importText}
          onChange={e => setImportText(e.target.value)}
          placeholder='[{"name":"功率过高通用处置","match_type":"parameter","match_key":"active_power","cause":"负荷突增","suggestion":"核对负荷与变比","level_suggestion":"警告"}]'
        />
      </Modal>
    </Modal>
  );
}
